<template>
  <BlBackdrop
    title="Edit access permission"
    description="Manage user access to the client database with tailored restrictions."
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div class="flex flex-col gap-5 px-8" :style="{ width: '40rem' }">
      <div class="grid grid-cols-2 gap-4">
        <div
          v-for="opt in typeOptions"
          :key="opt.value"
          class="py-4 px-4 flex flex-col items-center justify-center gap-2 flex-wrap rounded border cursor-pointer"
          :class="[
            typeInput === opt.value ? 'border-light-purple' : 'border-slate-400'
          ]"
          @click="typeInput = opt.value"
        >
          <BlIcon
            :class="{ 'opacity-50': typeInput !== opt.value }"
            :icon="opt.icon"
            class="w-6 h-6"
          />
          <div class="flex flex-col text-center">
            <BlTextBody2
              :class="{ 'text-light-purple': typeInput === opt.value }"
              :text="opt.label"
              class="font-bold"
            />
            <BlTextBody2 :text="opt.desc" />
          </div>
        </div>
      </div>

      <div v-if="typeInput === 'all_clients'" class="flex flex-col gap-1">
        <div class="py-2 px-4 flex flex-col gap-1 rounded bg-cornflower-200">
          <div class="flex items-center gap-2">
            <BlIcon icon="pin" color="cornflower-8" class="h-4 w-4" />
            <BlTextSubtitle3
              text="Permission overview"
              class="text-cornflower-800"
            />
          </div>
          <div>
            <BlTextSubtitle3 :text="user.name" class="text-cornflower-800" />
            <BlTextBody2
              text="will have access to"
              class="text-cornflower-800"
            />
            <BlTextSubtitle3
              text="all clients across the center."
              class="text-cornflower-800"
            />
          </div>
        </div>
      </div>

      <div v-if="typeInput === 'assigned_clients'" class="flex flex-col gap-4">
        <div>
          <div>
            <BlTextSubtitle3 text="Include any relevant tags" />
            <BlTextBody2 text="(optional)" />
          </div>
          <div>
            <BlTextBody2 text="Can also view clients that" />
            <BlTextBody2 text="have at least one" class="font-bold" />
            <BlTextBody2 text="of the specified tags." />
          </div>
        </div>
        <BlSearchBox
          type="branch"
          label="Branch tags"
          placeholder="Type tag"
          v-model="branchIdsInput"
          :options="branchOptions"
          @change="branchIdsInput = $event"
        />
        <BlSearchBox
          type="tag"
          label="Client tags"
          placeholder="Type tag"
          v-model="labelIdsInput"
          :options="labelOptions"
          @change="labelIdsInput = $event"
        />
        <div class="py-2 px-4 flex flex-col gap-1 rounded bg-cornflower-200">
          <div class="flex items-center gap-2">
            <BlIcon icon="pin" color="cornflower-8" class="h-4 w-4" />
            <BlTextSubtitle3
              text="Permission overview"
              class="text-cornflower-800"
            />
          </div>
          <div>
            <BlTextSubtitle3 :text="user.name" class="text-cornflower-800" />
            <BlTextBody2 text="will have access" class="text-cornflower-800" />
            <BlTextSubtitle3
              v-if="!totalTagAccesses"
              text="only to clients directly assigned to them."
              class="text-cornflower-800"
            />
            <span v-else>
              <BlTextSubtitle3
                text="only to clients directly assigned to them,"
                class="text-cornflower-800"
              />
              <BlTextBody2
                text="as well as clients tagged with at least one of"
                class="text-cornflower-800"
              />
              <BlTextSubtitle3
                :text="totalTagAccesses"
                class="text-cornflower-800"
              />
              <BlTextBody2
                text="selected tag(s)."
                class="text-cornflower-800"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlBackdrop from "../BlBackdrop.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";

import BlSearchBox from "../BlSearchBox.vue";

export default {
  name: "EditUserAccessModal",
  components: {
    BlBackdrop,
    BlTextBody2,
    BlTextSubtitle3,
    BlIcon,
    BlSearchBox
  },
  props: {
    user: { type: Object, required: true },
    centerId: { type: Number, default: 0 }
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      typeInput: this.user.restriction_type,
      typeOptions: [
        {
          value: "all_clients",
          icon: "people",
          label: "All clients",
          desc: "Can access all clients in the database."
        },
        {
          value: "assigned_clients",
          icon: "account-circle",
          label: "Assigned clients",
          desc: "Can only access clients assigned to them."
        }
      ],
      branchIdsInput: this.user.branch_accesses.map(i => i.id),
      labelIdsInput: this.user.user_tags.map(i => i.label_id)
    };
  },
  computed: {
    ...mapGetters(["currentUser", "branches", "labels"]),
    branchOptions() {
      return this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
    },
    labelOptions() {
      return this.labels.map(i => ({
        value: i.id,
        label: i.name
      }));
    },
    totalTagAccesses() {
      return this.branchIdsInput.length + this.labelIdsInput.length;
    },
    isDisabledSubmit() {
      return false;
    }
  },
  watch: {},
  created() {
    let params = "?sort=name_asc";
    if (this.centerId) {
      params += `&center_id=${this.centerId}`;
    }
    this.getBranches({ params }); // for get all branches
    this.getLabels({ params }); // for get all tags
  },
  mounted() {},
  methods: {
    ...mapActions(["getBranches", "getLabels", "updateUser"]),
    async onSubmit() {
      const params = {
        id: this.user.id,
        user: {
          restriction_type: this.typeInput,
          branch_access_ids: this.branchIdsInput,
          label_ids: this.labelIdsInput
        },
        title: "Access updated",
        message: "Success! The access for this user has been updated."
      };

      this.submitLoading = true;
      const { success } = await this.updateUser(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/team/EditUserAccessModal.vue